import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { activeCatalogSelector, Catalog } from '@vi-succ/products-domain';
import {
  SparepartAutocompleteResult,
  SparepartDataService,
  SparepartSearchHistoryResponse,
  SparepartSearchHistoryResult,
  SparepartSearchOptions,
  SparepartSearchResponse,
} from '@vi-succ/sparepart-domain';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';
import { SparepartAutocompleteEntity, SparepartSearchEntity, SparepartSearchResponseEntity } from '../entity/sparepart';
import { environment } from '../environments/environment';
import { EtProductDetailDataService } from './et-product-detail.data.service';
import { EtShoppingCartDataService } from './et-shopping-cart-data.service';
import { EtSparepartSearchHistoryPersistenceService } from './et-sparepart-search-history-persistence.service';

@Injectable({ providedIn: 'root' })
export class EtSparepartDataService implements SparepartDataService {
  private activeSortField: string | undefined;
  private activeSortDirection: string | undefined;

  constructor(
    private http: HttpClient,
    private store: Store,
    private searchHistoryPersistenceService: EtSparepartSearchHistoryPersistenceService,
    private shoppingCartDataService: EtShoppingCartDataService,
    private productDetailDataService: EtProductDetailDataService,
  ) {}

  searchSpareparts(query: string, pageIndex: number, pageSize: number): Observable<SparepartSearchResponse> {
    return this.store.select(activeCatalogSelector).pipe(
      filter((catalog) => !!catalog),
      first(),
      map((catalog) => catalog as Catalog),
      switchMap((catalog) => {
        return this.http
          .get<SparepartSearchResponseEntity>(
            `${environment.backend.url}/product/${catalog.salesOrg}/${catalog.language}/search`,
            {
              params: {
                searchToken: query,
                pageIndex,
                pageSize,
              },
              headers: {
                'X-API-KEY': environment.backend.apiKey,
                accept: 'application/json',
              },
            },
          )
          .pipe(
            map((response) => {
              const entities: SparepartSearchEntity[] = response.hits;
              const historyEntry: SparepartSearchHistoryResult = {
                createdDate: new Date().toISOString(),
                description: entities.length === 1 ? (entities[0].shortText ?? entities[0].longText) : '',
                id: this.searchHistoryPersistenceService.getNextId(),
                searchType: 'SPAREPART',
                term: query,
                totalResults: entities.length,
                materialNumber: entities.length === 1 ? entities[0].materialNumber : '',
                sellable:
                  entities.length === 1
                    ? entities[0].sellable && (entities[0].sparePart || entities[0].wearPart)
                    : false,
              };
              this.searchHistoryPersistenceService.saveEntry(historyEntry);
              const hits = entities.map((entity) => {
                return {
                  materialNumber: entity.materialNumber,
                  description: entity.shortText,
                  image_url: entity.thumbnail,
                };
              });
              return {
                total: response.total,
                hits,
              };
            }),
          );
      }),
    );
  }

  searchHistory(options: SparepartSearchOptions): Observable<SparepartSearchHistoryResponse> {
    this.activeSortField = options.sortField ?? this.activeSortField ?? 'createdDate';
    this.activeSortDirection = options.sortDirection ?? this.activeSortDirection ?? 'desc';

    const data = this.searchHistoryPersistenceService.getPagedResults({
      pageIndex: options.pageIndex ?? 0,
      pageSize: options.pageSize ?? 10,
      sortField: this.activeSortField,
      sortDirection: this.activeSortDirection,
    });

    return of({
      total: this.searchHistoryPersistenceService.getSize(),
      results: data,
    });
  }

  autocomplete(query: string): Observable<SparepartAutocompleteResult[]> {
    return this.store.select(activeCatalogSelector).pipe(
      filter((catalog) => !!catalog),
      first(),
      map((catalog) => catalog as Catalog),
      switchMap((catalog) => {
        return this.http
          .get<SparepartAutocompleteEntity[]>(
            `${environment.backend.url}/product/${catalog.salesOrg}/${catalog.language}/complete`,
            {
              params: {
                searchToken: query,
              },
              headers: {
                accept: 'application/vnd.complete.v2+json',
              },
            },
          )
          .pipe(
            map((entities) => {
              return entities.map((entity) => {
                return {
                  materialNumber: entity._source.materialNumber,
                  description: entity._source.shortText,
                };
              });
            }),
          );
      }),
    );
  }

  deleteSparepartHistoryItem(sparePart: SparepartSearchHistoryResult): Observable<SparepartSearchHistoryResult> {
    this.searchHistoryPersistenceService.deleteEntryById(sparePart.id);
    return of(sparePart);
  }

  deleteAllSparepartHistory(): Observable<SparepartSearchHistoryResponse> {
    this.searchHistoryPersistenceService.deleteAllEntries();
    return of({
      total: 0,
      results: [],
    });
  }

  addSparepartToCart(materialNumber: string): Observable<void> {
    return this.store.select(activeCatalogSelector).pipe(
      filter((catalog) => !!catalog),
      first(),
      map((catalog) => catalog as Catalog),
      switchMap((catalog) => {
        return this.productDetailDataService.getProductDetail(catalog.salesOrg, catalog.language, materialNumber).pipe(
          switchMap((product) => {
            return this.shoppingCartDataService
              .addItemToShoppingCartFromProduct(product, 1, undefined, true)
              .pipe(map(() => void 0));
          }),
        );
      }),
    );
  }
}
